import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/Button';

const ComplianceSettingsModal = ({ isOpen, onClose, onSave, initialSettings }) => {
  const [settings, setSettings] = useState(initialSettings || {
    essential: true, // Always true
    functional: false,
    analytical: false,
    marketing: false
  });

  const toggleSetting = (key) => {
    if (key === 'essential') return;
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    onSave(settings);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
          />
          
          <motion.div
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]"
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-indigo-50/30">
              <div className="flex items-center gap-3">
                <div className="bg-indigo-600 p-2 rounded-xl shadow-lg shadow-indigo-200">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h2 className="text-xl font-extrabold text-gray-900 tracking-tight">Privacy Voorkeuren</h2>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto space-y-6">
              <p className="text-sm text-gray-500 font-medium">
                Beheer hoe wij informatie verzamelen. Noodzakelijke technieken kunnen niet worden uitgeschakeld om de werking van het platform te garanderen.
              </p>

              <div className="space-y-4">
                {/* Essential */}
                <div className="flex items-start justify-between gap-4 p-4 rounded-2xl bg-gray-50 border border-gray-100 opacity-80">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                      Strikt Noodzakelijk
                      <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded uppercase tracking-wider">Altijd actief</span>
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">Essentieel voor beveiliging, inloggen en basisfuncties.</p>
                  </div>
                  <div className="relative inline-flex items-center cursor-not-allowed">
                    <div className="w-11 h-6 bg-indigo-600 rounded-full shadow-inner" />
                    <div className="absolute left-6 w-4 h-4 bg-white rounded-full transition-transform" />
                  </div>
                </div>

                {/* Functional */}
                <label className="flex items-start justify-between gap-4 p-4 rounded-2xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/10 transition-all cursor-pointer">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-sm">Functioneel</h4>
                    <p className="text-xs text-gray-500 mt-1">Onthouden van voorkeuren zoals taalinstellingen en chatgeschiedenis.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.functional}
                    onChange={() => toggleSetting('functional')}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
                </label>

                {/* Analytical */}
                <label className="flex items-start justify-between gap-4 p-4 rounded-2xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/10 transition-all cursor-pointer">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-sm">Analytisch</h4>
                    <p className="text-xs text-gray-500 mt-1">Helpt ons te begrijpen hoe bezoekers het platform gebruiken (geanonimiseerd).</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.analytical}
                    onChange={() => toggleSetting('analytical')}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
                </label>

                {/* Marketing */}
                <label className="flex items-start justify-between gap-4 p-4 rounded-2xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50/10 transition-all cursor-pointer">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-sm">Marketing</h4>
                    <p className="text-xs text-gray-500 mt-1">Toon relevante advertenties en aanbiedingen op basis van uw interesses.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings.marketing}
                    onChange={() => toggleSetting('marketing')}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600" />
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-3">
              <Button
                variant="primary"
                className="flex-1 !rounded-2xl"
                onClick={handleSave}
              >
                Keuze opslaan
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ComplianceSettingsModal;
